import { Component, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ILogin, IFirma, ApiClientService } from '../../services/api-client.service';
import { CacheService } from '../../services/cache.service';
import * as CryptoJS from 'crypto-js';
import { UserService } from '../../services/user.service';
import { DxTextBoxComponent } from 'devextreme-angular';

@Component({
  selector: 'app-korisnici',
  templateUrl: './korisnici.component.html',
  styleUrls: ['./korisnici.component.scss']
})
export class KorisniciComponent {
  @ViewChild('korisniciGrid', { static: false }) korisniciGrid: any;

  gridData: any[] = [];
  firmeData: IFirma[] = [];
  loading = false;
  isEditPopupVisible = false;
  isCreatePopupVisible = false;
  selectedKorisnik: any | null = null;
  loadPanelPosition = { of: '#korisniciGrid' };
  isUserAgroAdmin = false;
  korisnikForm!: FormGroup;

  @ViewChild('passwordInput') passwordInput!: DxTextBoxComponent;
  passwordMode: 'password' | 'text' = 'password';
  passwordButtons: any[] = [];

  constructor(
    private apiClient: ApiClientService,
    private fb: FormBuilder,
    private cache: CacheService,
    private userService: UserService
  ) {
    this.initializePasswordButton();
    this.boolToYesNo = this.boolToYesNo.bind(this);
    this.initializeForm();
  }

  ngOnInit() {
    this.isUserAgroAdmin = this.userService.isUserAgroAdmin();
    this.loadKorisnici();
    this.loadFirme();
  }

  initializeForm() {
    this.korisnikForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      firma: [null, Validators.required],
      aktivan: [1], // This can stay as number
      userAgro: [false], // These should be booleans
      userTransport: [false],
      modAgro: [false],
      modTransport: [false],
      ownerAgro: [false],
      ownerTransport: [false],
      deviceId: [null],
      mobileAppVersion: [null]
    });
  }

  // Add these properties
  searchTimeout: any;
  firmaFilteredData: IFirma[] = [];

  // Add this method
  onFirmaSearch(e: any) {
    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      const searchValue = e.value?.toLowerCase();
      if (!searchValue) {
        this.firmaFilteredData = [];
      } else {
        // Filter only first 100 results for performance
        this.firmaFilteredData = this.firmeData
          .filter(f => f.naziv?.toLowerCase().includes(searchValue))
          .slice(0, 100);
      }
    }, 300);
  }

  async loadKorisnici() {
    this.loading = true;
    try {
      const data = await this.apiClient.loginsAll();

      // Transform from UserLoginModel (backend response) to grid format
      this.gridData = data.map(item => ({
        id: item.id,
        username: item.username,
        firma: item.firma,
        firmaNaziv: this.getFirmaNameStatic(item.firma),
        aktivan: !!item.aktivan, // Convert number to boolean for display
        user_agro: !!item.userAgro, // Map UserAgro -> user_agro
        user_transport: !!item.userTransport,
        mod_agro: !!item.modAgro,
        mod_transport: !!item.modTransport,
        owner_agro: !!item.ownerAgro,
        owner_transport: !!item.ownerTransport,
        device_id: item.deviceId,
        mobile_app_version: item.mobileAppVersion
      }));

    } catch (error) {
      console.error('Error loading korisnici:', error);
    } finally {
      this.loading = false;
    }
  }

  // Static version that doesn't depend on component state
  private getFirmaNameStatic(firmaId: number): string {
    const firma = this.firmeData.find(f => f.id === firmaId);
    return firma ? firma.naziv as string : 'Nepoznato';
  }

  async loadFirme() {
    try {
      this.firmeData = await this.cache.getCachedOrFetch('firme', () => this.apiClient.firmeAll(10000, 0));
    } catch (error) {
      console.error('Error loading firme:', error);
    }
  }

  boolToYesNo(rowData: any): string {
    return rowData.aktivan ? 'Da' : 'Ne';
  }

  onKorisnikRowClick(e: any) {
    if (e.data) {
      this.selectedKorisnik = e.data;
    }
  }

  onAddClick() {
    this.selectedKorisnik = null;
    this.korisnikForm.reset({
      aktivan: 1,
      userAgro: false,
      userTransport: false,
      modAgro: false,
      modTransport: false,
      ownerAgro: false,
      ownerTransport: false
    });
    this.isCreatePopupVisible = true;
  }

  onEditClick() {
    if (this.selectedKorisnik) {
      this.populateForm(this.selectedKorisnik);
      this.isEditPopupVisible = true;
    }
  }

  populateForm(korisnik: any) {
    this.korisnikForm.patchValue({
      username: korisnik.username,
      password: korisnik.password || '', // Password might be empty in grid data
      firma: korisnik.firma,
      aktivan: korisnik.aktivan ? 1 : 0, // Convert back to number for form
      userAgro: !!korisnik.user_agro, // Map user_agro -> userAgro
      userTransport: !!korisnik.user_transport,
      modAgro: !!korisnik.mod_agro,
      modTransport: !!korisnik.mod_transport,
      ownerAgro: !!korisnik.owner_agro,
      ownerTransport: !!korisnik.owner_transport,
      deviceId: korisnik.device_id,
      mobileAppVersion: korisnik.mobile_app_version
    });
  }

  async createKorisnik() {
    if (this.korisnikForm.valid) {
      this.loading = true;
      try {
        const formData = this.transformToBackendModel(this.korisnikForm.value);
        await this.apiClient.loginsPOST(formData);
        this.isCreatePopupVisible = false;
        this.korisnikForm.reset();
        this.loadKorisnici(); // Refresh grid
      } catch (error) {
        console.error('Error creating korisnik:', error);
      } finally {
        this.loading = false;
      }
    }
  }

  async updateKorisnikMobileDeviceId() {
    this.korisnikForm.patchValue({ deviceId: null });
  }

  async updateKorisnik() {
    if (this.korisnikForm.valid && this.selectedKorisnik) {
      this.loading = true;
      try {
        const formData = this.transformToBackendModel(this.korisnikForm.value);
        formData.id = this.selectedKorisnik.id;
        await this.apiClient.loginsPUT(this.selectedKorisnik.id as number, formData);
        this.isEditPopupVisible = false;
        this.loadKorisnici(); // Refresh grid
      } catch (error) {
        console.error('Error updating korisnik:', error);
      } finally {
        this.loading = false;
      }
    }
  }

  // Transform from frontend form model to backend Login model
  private transformToBackendModel(formData: any): any {
    const hashedPassword = formData.password
      ? CryptoJS.MD5(formData.password).toString()
      : undefined;

    return {
      id: formData.id,
      username: formData.username,
      password: hashedPassword,
      firma: formData.firma,
      aktivan: formData.aktivan ? 1 : 0, // Keep as number
      user_agro: formData.userAgro, // Map userAgro -> user_agro
      user_transport: formData.userTransport,
      mod_agro: formData.modAgro,
      mod_transport: formData.modTransport,
      owner_agro: formData.ownerAgro,
      owner_transport: formData.ownerTransport,
      device_id: formData.deviceId,
      mobile_app_version: formData.mobileAppVersion
      // Don't include FirmaObject - it's read-only from backend
    };
  }

  onRefreshClick() {
    this.loadKorisnici();
  }

  onEditPopupHidden() {
    this.isEditPopupVisible = false;
    this.selectedKorisnik = null;
  }

  onCreatePopupHidden() {
    this.isCreatePopupVisible = false;
  }

  // Helper method to get firma name for display
  getFirmaName(rowData: any): string {
    const firma = this.firmeData.find(f => f.id === rowData.firma);
    return firma ? firma.naziv as string : 'Nepoznato';
  }

  initializePasswordButton() {
    this.passwordButtons = [{
      name: 'showPassword',
      location: 'after',
      options: {
        icon: 'eyeopen',
        type: 'default',
        stylingMode: 'text',
        onClick: () => this.togglePasswordVisibility()
      }
    }];
  }

  togglePasswordVisibility() {
    this.passwordMode = this.passwordMode === 'password' ? 'text' : 'password';

    // Update button icon
    this.passwordButtons[0].options.icon = this.passwordMode === 'password' ? 'eyeopen' : 'eyeclose';

    // Refresh the buttons array to trigger change detection
    this.passwordButtons = [...this.passwordButtons];
  }
}
